import React, { forwardRef, useState } from "react";
import { Spinner } from "./Spinner";

const VARIANT = {
  primary: "bg-a-primary text-white hover:brightness-110",
  secondary: "bg-a-dim text-a-primary hover:brightness-95",
  danger: "bg-a-danger text-white hover:brightness-110",
  ghost: "border border-a-border bg-transparent text-a-text hover:bg-a-text/5",
};
const SIZE = {
  sm: "px-3 py-2 text-[13px]",
  md: "px-4 py-3 text-[15px]",
  lg: "px-5 py-4 text-base",
};

export function Button({ label, onPress, variant = "primary", loading, disabled, icon, className = "", testID, fullWidth = true, size = "md", type = "button" }) {
  const fg = variant === "primary" || variant === "danger" ? "#fff" : undefined;
  return (
    <button
      type={type}
      data-testid={testID}
      disabled={disabled || loading}
      onClick={onPress}
      className={`btn-press inline-flex items-center justify-center rounded-xl font-bold ${VARIANT[variant]} ${SIZE[size]} ${fullWidth ? "w-full" : ""} ${disabled ? "opacity-55" : ""} disabled:cursor-not-allowed ${className}`}
    >
      {loading ? (
        <Spinner size={20} color={fg} className={fg ? "" : "!text-current"} />
      ) : (
        <span className="flex items-center gap-2">{icon}{label}</span>
      )}
    </button>
  );
}

export const Input = forwardRef(function Input(
  { label, helper, errorText, leading, trailing, testID, multiline, rows = 4, className = "", onChangeText, onSubmitEditing, inputClassName = "", ...rest },
  ref,
) {
  const [focused, setFocused] = useState(false);
  const Tag = multiline ? "textarea" : "input";
  return (
    <div className={`flex w-full flex-col gap-1.5 ${className}`}>
      {label ? <label className="text-xs font-semibold uppercase tracking-[0.4px] text-a-muted" data-testid={testID ? `${testID}-label` : undefined}>{label}</label> : null}
      <div
        className={`flex min-h-[48px] items-center rounded-xl border bg-a-surface px-3 transition-colors ${
          errorText ? "border-a-danger" : focused ? "border-a-primary" : "border-a-border"
        }`}
      >
        {leading ? <span className="flex px-1 text-a-muted">{leading}</span> : null}
        <Tag
          ref={ref}
          data-testid={testID}
          rows={multiline ? rows : undefined}
          {...rest}
          onChange={(e) => onChangeText?.(e.target.value)}
          onFocus={(e) => { setFocused(true); rest.onFocus?.(e); }}
          onBlur={(e) => { setFocused(false); rest.onBlur?.(e); }}
          onKeyDown={(e) => { if (e.key === "Enter" && !multiline && onSubmitEditing) { e.preventDefault(); onSubmitEditing(); } }}
          className={`w-full flex-1 bg-transparent py-3 text-[15px] text-a-text outline-none placeholder:text-a-muted ${multiline ? "resize-y" : ""} ${inputClassName}`}
        />
        {trailing ? <span className="flex px-1 text-a-muted">{trailing}</span> : null}
      </div>
      {errorText ? (
        <p className="text-xs text-a-danger" data-testid={testID ? `${testID}-error` : undefined}>{errorText}</p>
      ) : helper ? <p className="text-xs text-a-muted">{helper}</p> : null}
    </div>
  );
});

export function Card({ children, className = "", padded = true, testID, onClick, style }) {
  return (
    <div
      data-testid={testID}
      onClick={onClick}
      style={style}
      className={`rounded-[14px] border border-a-border bg-a-surface ${padded ? "p-4" : ""} ${onClick ? "card-hover cursor-pointer" : ""} ${className}`}
    >
      {children}
    </div>
  );
}

export function ChipRow({ options, value, onChange, testID, className = "" }) {
  return (
    <div className={`no-scrollbar flex h-14 items-center gap-2 overflow-x-auto ${className}`} data-testid={testID}>
      {options.map((opt) => {
        const active = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.key)}
            data-testid={`${testID || "chip"}-${opt.key}`}
            className={`h-9 shrink-0 whitespace-nowrap rounded-full border px-3.5 text-[13px] font-semibold transition-colors ${
              active ? "border-a-chipActive bg-a-chipActive text-white" : "border-a-border bg-a-chip text-a-text hover:border-a-primary/50"
            }`}
          >
            {opt.label}{typeof opt.count === "number" ? `  ${opt.count}` : ""}
          </button>
        );
      })}
    </div>
  );
}

export function Toggle({ value, onChange, disabled, testID }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      data-testid={testID}
      disabled={disabled}
      onClick={() => onChange(!value)}
      className={`relative h-[26px] w-[46px] shrink-0 rounded-full p-[3px] transition-colors ${value ? "bg-a-primary" : "bg-a-border"} ${disabled ? "opacity-60" : ""}`}
    >
      <span className={`block h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${value ? "translate-x-5" : "translate-x-0"}`} />
    </button>
  );
}

export const initialsOf = (name) => (name || "?").split(" ").filter(Boolean).map((n) => n[0]).join("").slice(0, 2).toUpperCase();

export function Avatar({ src, name, size = 40, variant = "solid", className = "" }) {
  if (src) return <img src={src} alt={name || "avatar"} className={`shrink-0 rounded-full object-cover ${className}`} style={{ width: size, height: size }} />;
  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full font-bold ${variant === "solid" ? "bg-a-primary text-white" : "bg-a-dim text-a-primary"} ${className}`}
      style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.32)) }}
    >
      {initialsOf(name)}
    </div>
  );
}

export function Overline({ children, className = "", muted }) {
  return <p className={`ov text-[11px] font-bold uppercase tracking-[1.5px] ${muted ? "text-a-muted" : "text-a-primary"} ${className}`}>{children}</p>;
}
